import { Button, Form } from "antd";
import { useWatch } from "antd/es/form/Form";
import InfoTooltip from "components/Common/InfoTooltip";
import useThemes from "hooks/useThemes";
import { SectionComponent } from "pages/NewBusinessOnboarding/businessFormConstants";
import OnboardingThemeCard from "pages/NewBusinessOnboarding/components/OnboardingThemeCard";
import { useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";

const BusinessDesign: SectionComponent = ({ form }) => {
  const [showAllThemes, setShowAllThemes] = useState(false);
  const { themes, isLoading: globalThemesLoading } = useThemes();
  const preSelectedThemes = themes
    .filter((theme) => theme.preselected)
    .map((theme) => theme.name);

  const selectedThemes = useWatch("themes", form) || preSelectedThemes;

  const sortedThemesSelectedFirst = useMemo(() => {
    // Ensure themes is an array before sorting
    const themesArray = Array.isArray(themes) ? themes : [];

    // Create a copy before sorting
    return [...themesArray].sort((a, b) => {
      const aName = a.name;
      const bName = b.name;

      const aIsSelected = selectedThemes.includes(aName);
      const bIsSelected = selectedThemes.includes(bName);

      if (aIsSelected && !bIsSelected) {
        return -1; // a comes first
      } else if (!aIsSelected && bIsSelected) {
        return 1; // b comes first
      } else {
        // If selection status is the same, sort alphabetically
        return aName.localeCompare(bName);
      }
    });
  }, [themes, selectedThemes]);

  const handleToggle = (name: string) => {
    const currentThemes = form.getFieldValue("themes") || [];
    form.setFieldValue(
      "themes",
      currentThemes.includes(name)
        ? currentThemes.filter((item: string) => item !== name)
        : [...currentThemes, name]
    );
  };

  return (
    <div className="min-h-[400px]">
      <Form.Item
        name="themes"
        label={
          <>
            Themes
            <InfoTooltip
              title="Choose how you want your post graphics to look."
              className="text-antd-colorIcon"
            />
          </>
        }
      >
        {globalThemesLoading ? (
          <Skeleton
            count={3}
            width={100}
            height={100}
            containerClassName="flex flex-wrap gap-1"
          />
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 lg:grid-cols-4 gap-2">
              {sortedThemesSelectedFirst.map(
                (theme) =>
                  (showAllThemes || selectedThemes.includes(theme.name)) && (
                    <OnboardingThemeCard
                      key={theme.name}
                      {...{
                        theme,
                        isSelected: selectedThemes.includes(theme.name),
                        handleSelect: handleToggle,
                      }}
                    />
                  )
              )}
            </div>
            <Button
              size="small"
              onClick={() => setShowAllThemes(!showAllThemes)}
            >
              {showAllThemes ? "Collapse" : "Show All Themes"}
            </Button>
          </div>
        )}
      </Form.Item>
    </div>
  );
};

export default BusinessDesign;
